import { useEffect, useMemo, useState } from 'react'
import {
  DialogActions,
  DialogContent,
  DialogTitle,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Stack,
  Typography,
} from '@mui/material'
import { useTheme } from '@mui/material/styles'
import { useCurrentStore, useEditOrderItem, useOrdering } from 'hooks'
import {
  MenuItem,
  MenuModifier,
  MenuOption,
  OrderModifier,
  OrderOption,
} from 'models'
import { useTranslation } from 'react-i18next'
import {
  formatSelectionLevel,
  isOrderModifersSatisfied,
  isRequiredSelectionLevel,
  loadMenuItemByOrderItem,
  loadMenuModifiersByOrderItem,
  maxOptionCountForSelectionLevel,
  newOrderModifier,
  newOrderOption,
} from 'services'
import { isEmpty } from 'lodash'
import { OrderItemQuantity } from 'components/ordering/OrderItemQuantity'
import { KFieldLabel } from '../core/KFieldLabel'
import { KCheckIconButton } from '../core/KCheckIconButton'
import { KDialog } from 'components/core/KDialog'
import { KTextField } from 'components/core/KTextField'
import { KMoneyField } from 'components/core/KMoneyField'
import { KButton } from 'components/core/KButton'
import { KDialogCloseButton } from './KDialogCloseButton'

const ModifierLabel = ({ modifier }: { modifier: MenuModifier }) => {
  const { t } = useTranslation()

  const state = useMemo(() => {
    const required = isRequiredSelectionLevel(modifier.selection_level)
    const prompt = formatSelectionLevel(modifier.selection_level)
    const hasPrompt = !isEmpty(prompt)

    return {
      required,
      hasPrompt,
      prompt: hasPrompt ? t(`(select ${prompt})`) : '',
    }
  }, [modifier, t])

  return (
    <KFieldLabel>
      {modifier.name}
      {state.required && (
        <Typography
          component="span"
          color="error"
          sx={{ display: 'inline', marginLeft: 0.5 }}
        >
          *
        </Typography>
      )}
      {state.hasPrompt && (
        <Typography
          component="span"
          variant="body2"
          color={state.required ? 'error' : 'secondary'}
          sx={{ paddingX: 2 }}
        >
          {state.prompt}
        </Typography>
      )}
    </KFieldLabel>
  )
}

interface EditOrderItemState {
  count: number
  note?: string
  notePrice?: number
  orderModifiers: OrderModifier[]
  menuModifiers: MenuModifier[]
  menuItem?: MenuItem
  canSave: boolean
}

const initialState = {
  count: 1,
  note: '',
  notePrice: 0,
  orderModifiers: [],
  menuModifiers: [],
  canSave: false,
} as EditOrderItemState

export const EditOrderItemDialog = () => {
  const { t } = useTranslation()
  const theme = useTheme()

  const { store } = useCurrentStore()
  const { order, saveOrder } = useOrdering()
  const { editingOrderItem: oItem, clearEditingOrderItem } = useEditOrderItem()

  const { formatCurrency } = useCurrentStore()

  const [state, setState] = useState(initialState)

  useEffect(() => {
    if (!oItem || !store) {
      setState(initialState)
      return
    }

    const menuItem = loadMenuItemByOrderItem(oItem, store.categories ?? [])
    const menuModifiers = loadMenuModifiersByOrderItem(
      oItem,
      store.categories ?? [],
      store.modifiers ?? [],
    )
    const canSave = isOrderModifersSatisfied(oItem.modifiers, menuModifiers)
    setState({
      count: oItem.count,
      note: oItem.note,
      notePrice: oItem.price_note,
      orderModifiers: [...oItem.modifiers],
      menuModifiers,
      menuItem,
      canSave,
    })
  }, [oItem, store])

  const increaseQuantity = () => {
    if (state.count < 99) {
      setState((state) => ({ ...state, count: state.count + 1 }))
    }
  }

  const decreaseQuantity = () => {
    if (state.count > 1) {
      setState({ ...state, count: state.count - 1 })
    }
  }

  const getOptionName = (mModifier: MenuModifier, mOption: MenuOption) => {
    if (mModifier.prefix) {
      return mModifier.prefix + ' ' + mOption.name
    }
    return mOption.name
  }

  const isSelected = (mModifier: MenuModifier, mOption: MenuOption) => {
    const matchedOModifier = state.orderModifiers.find(
      (om) => om.id === mModifier.id,
    )
    if (!matchedOModifier) return false

    const matchedOOption = matchedOModifier.options?.find(
      (oo) => oo.id === mOption.id,
    )
    return matchedOOption !== undefined
  }

  const toggleOptionSelection = (
    mModifier: MenuModifier,
    mOption: MenuOption,
  ) => {
    const newOModifiers = [...state.orderModifiers]
    const matchedOModifierIdx = newOModifiers.findIndex(
      (om) => om.id === mModifier.id,
    )
    if (matchedOModifierIdx >= 0) {
      const matchedOModifier = newOModifiers[matchedOModifierIdx]
      const matchedOOption = matchedOModifier.options?.find(
        (oo) => oo.id === mOption.id,
      )
      let newOOptions: OrderOption[]
      if (matchedOOption) {
        newOOptions = matchedOModifier.options?.filter(
          (oo) => oo.id !== mOption.id,
        )
      } else {
        const maxCount = maxOptionCountForSelectionLevel(
          mModifier.selection_level,
        )
        newOOptions = [...matchedOModifier.options]
        if (maxCount > 0 && matchedOModifier.options.length === maxCount) {
          newOOptions.shift()
        }
        newOOptions.push(newOrderOption(mOption))
      }
      newOModifiers[matchedOModifierIdx] = {
        ...matchedOModifier,
        options: newOOptions,
      }
    } else {
      newOModifiers.push(newOrderModifier(mModifier, mOption))
    }
    setState({
      ...state,
      orderModifiers: newOModifiers,
      canSave: isOrderModifersSatisfied(newOModifiers, state.menuModifiers),
    })
  }

  const saveChanges = () => {
    if (!oItem || !store || !order) return

    const newOItem = {
      ...oItem,
      count: state.count,
      note: state.note,
      price_note: state.notePrice,
      modifiers: state.orderModifiers,
    }

    const existingIndex = order.items.findIndex((oi) => oi.id === oItem.id)
    const oItems = [...order.items]
    if (existingIndex >= 0) {
      oItems[existingIndex] = newOItem
    } else {
      oItems.push(newOItem)
    }

    saveOrder({ ...order, items: oItems })
    clearEditingOrderItem()
  }

  return (
    <KDialog open={oItem !== undefined} onClose={clearEditingOrderItem}>
      <DialogTitle variant="h3" sx={{ paddingX: 0 }}>
        {oItem?.name}
        {!isEmpty(oItem?.item_note) && (
          <Typography variant="body2" color="secondary" sx={{ marginTop: 1 }}>
            {oItem?.item_note}
          </Typography>
        )}
      </DialogTitle>
      <DialogContent dividers sx={{ paddingX: 0 }}>
        {!isEmpty(state.menuItem?.description) && (
          <Typography variant="body1" mb={2}>
            {t(state.menuItem!.description!)}
          </Typography>
        )}
        {store.show_special_note && (
          <Stack
            sx={{
              flexDirection: 'row',
              justifyContent: 'space-between',
              gap: 2,
              marginBottom: 4,
              [theme.breakpoints.down('md')]: {
                flexDirection: 'column',
              },
            }}
          >
            <KTextField
              label={t('Special note')}
              value={state.note}
              onChange={(e) => setState({ ...state, note: e.target.value })}
              placeholder={t('No spice | No onion | ...')}
              inputProps={{ maxLength: 100 }}
              sx={{ flexGrow: 1 }}
            />
            <KMoneyField
              label={t('Special note amount')}
              value={state.notePrice}
              onChange={(ev) =>
                setState({
                  ...state,
                  notePrice: ev.target.value as unknown as number,
                })
              }
              sx={{ width: 220 }}
            />
          </Stack>
        )}
        {!isEmpty(state.menuModifiers) && (
          <Stack
            sx={{
              alignItems: 'stretch',
              flexDirection: 'column',
              gap: 2,
            }}
          >
            <Typography variant="h3">{t('Other options')}</Typography>
            {state.menuModifiers.map((mModifier) => (
              <Stack key={mModifier.id} sx={{ marginBottom: 2 }}>
                <ModifierLabel modifier={mModifier} />
                <List>
                  {mModifier.options?.map((mOption) => (
                    <ListItem
                      secondaryAction={
                        <KCheckIconButton
                          checked={isSelected(mModifier, mOption)}
                          onClick={() =>
                            toggleOptionSelection(mModifier, mOption)
                          }
                        />
                      }
                      divider
                      disablePadding
                      key={mOption.id}
                    >
                      <ListItemButton
                        onClick={() =>
                          toggleOptionSelection(mModifier, mOption)
                        }
                      >
                        <ListItemText
                          id={mOption.id}
                          primary={getOptionName(mModifier, mOption)}
                          primaryTypographyProps={{
                            variant: 'h5',
                            marginBottom: 0.5,
                          }}
                          secondary={formatCurrency(mOption.price ?? 0)}
                        />
                      </ListItemButton>
                    </ListItem>
                  ))}
                </List>
              </Stack>
            ))}
          </Stack>
        )}
      </DialogContent>
      <DialogActions
        sx={{
          paddingX: 0,
          flexDirection: 'row',
          justifyContent: 'space-between',
        }}
      >
        <Stack direction="row" alignItems="center" spacing={2}>
          <KFieldLabel variant="h4">{t('Quantity')}</KFieldLabel>
          <OrderItemQuantity
            quantity={state.count}
            increaseQuantity={increaseQuantity}
            decreaseQuantity={decreaseQuantity}
          />
        </Stack>
        <KButton
          size="medium"
          autoFocus
          onClick={saveChanges}
          disabled={state.canSave === false}
        >
          {t('Save changes')}
        </KButton>
      </DialogActions>
      <KDialogCloseButton onClick={() => clearEditingOrderItem()} />
    </KDialog>
  )
}
